import cn from 'classnames';
import { Button, type ButtonProps } from '@nespresso-design-system/components';
import { useComputed } from '@preact/signals';
import { useId } from 'preact/hooks';
import useCardModalId from '../hooks/useCardModalId.tsx';
import useImpression from '@/widgets/hooks/useImpression.tsx';
import PromotionCardModal from '@/widgets/ProductList/modals/PromotionCardModal/PromotionCardModal.tsx';
import CardBase from '@/widgets/ProductList/cards/CardBase/CardBase';
import { CREATIVE_SLOT, RAISED_BY } from '@/widgets/ProductList/Tracking.ts';
import styles from './PromotionCard.module.scss';
import { trackSelectPromotion } from '@/widgets/services/TrackingService';
import { useIntersection } from '@/widgets/hooks/useIntersection';
import type { PromotionCardDefinition } from '../../config';
import type { PersistedState } from '@/widgets/ProductList/Store';
import useQueryStringStore from '@/widgets/hooks/useQueryStringStore';

type ModalContent = NonNullable<PromotionCardDefinition['modal']>;

export type PromotionCardCtaLinkTarget = 'same-tab' | 'new-tab';

export type PromotionCardProps = {
  onClick: (
    event: MouseEvent,
    url?: string,
    target?: PromotionCardCtaLinkTarget
  ) => void;
  promotion: PromotionCardDefinition;
  class?: string;
};

const overlayStyles = {
  full: styles.full,
  top: styles.top,
  bottom: styles.bottom
};

export default function PromotionCard({
  onClick,
  promotion,
  class: className
}: PromotionCardProps) {
  const autoId = useId();
  const id = promotion.promotionId ?? autoId;
  const overlay = promotion.backgroundOverlay;
  const mainCopy = promotion.description ?? '';
  const smallHeading = promotion.title ?? '';
  const {
    type = 'link',
    target,
    buttonPosition = 'right',
    label,
    url
  } = promotion.cta ?? {};

  const cardRef = useImpression<HTMLDivElement>(RAISED_BY, CREATIVE_SLOT, {
    promotionId: id,
    promotionName: promotion.promotionName,
    creativeName: promotion.promotionCreative
  });

  const bgImageIntersection = useIntersection<HTMLDivElement>({
    // 50px vertical margin to start loading the image
    rootMargin: '50px 0px',
    threshold: 0.1
  });

  const openInNewTab = target === 'new-tab';
  const hasModal = type === 'modal';
  const modal = promotion.modal ?? ({} as ModalContent);

  const [{ openModal }, setPersistedState] =
    useQueryStringStore<PersistedState>();
  const modalId = useCardModalId<`promotion-${string}`>('promotion-', id);
  const isOpen = useComputed(() => openModal.value === modalId.value);

  const onClose = () => setPersistedState({ openModal: undefined });

  const handleClick = () => {
    if (hasModal) {
      setPersistedState({ openModal: modalId.peek() });
    }

    trackSelectPromotion({
      promotionId: id,
      promotionName: promotion.promotionName,
      creativeName: promotion.promotionCreative,
      creativeSlot: CREATIVE_SLOT,
      ctaName: label ?? 'Unknown label'
    });
  };

  return (
    <>
      <CardBase
        onClick={(event) => onClick(event, url, target)}
        className={styles.container}
      >
        <div
          ref={cardRef}
          class={cn(styles.card, className, {
            [styles.dark]: promotion.backgroundTheme === 'dark',
            [styles.light]: promotion.backgroundTheme !== 'dark'
          })}
        >
          <div
            class={cn(styles.bgImage, {
              [styles.hoverEffect]: !promotion.disableHoverEffect
            })}
            ref={bgImageIntersection.elementRef}
            style={
              bgImageIntersection.hasFired && promotion.backgroundImage
                ? {
                    '--promotion-card-background-image': `url(${promotion.backgroundImage})`
                  }
                : {}
            }
          />
          {overlay && overlay !== 'none' && (
            <div class={cn(styles.overlay, overlayStyles[overlay])} />
          )}
          <div class={styles.inner}>
            <div class={styles.content}>
              {smallHeading && <h2 class={styles.heading}>{smallHeading}</h2>}
              {mainCopy && <p class={styles.copy}>{mainCopy}</p>}
            </div>
            {label && (
              <div
                class={cn(styles.ctaArea, {
                  [styles.center]: buttonPosition === 'center',
                  [styles.right]: buttonPosition === 'right'
                })}
              >
                <Button
                  href={hasModal ? undefined : url}
                  label={label ?? ''}
                  target={openInNewTab ? '_blank' : '_self'}
                  rel={openInNewTab ? 'noreferrer' : ''}
                  size={'medium' as ButtonProps['size']}
                  variant="secondary"
                  contrast={promotion.backgroundTheme}
                  className={styles.button}
                  onClick={handleClick}
                />
              </div>
            )}
          </div>
        </div>
      </CardBase>
      {hasModal && (
        <PromotionCardModal
          copy={modal.copy}
          images={modal.images}
          imageAlt={modal.imageAlt}
          heading={modal.heading}
          disclaimer={modal.disclaimerText}
          label={label ?? ''}
          url={url ?? ''}
          isOpen={isOpen}
          openInNewTab={openInNewTab}
          onClose={onClose}
        />
      )}
    </>
  );
}
